import React, { useState, Component } from 'react';
import { Check, Star, Shield, BookOpen, Heart, ArrowRight, Lock, Menu, X, ChevronDown } from 'lucide-react';
// Color Palette Constants
const COLORS = {
  teal: '#0f4c4c',
  cream: '#faf8f5',
  gold: '#d4a574',
  goldHover: '#b88b5c',
  textDark: '#1a2e2e',
  textLight: '#f0fdfa'
};
// Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-0">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-1" />

      {/* Modal Content */}
      <div className="relative bg-[#faf8f5] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" data-id="element-2">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#0f4c4c]/10" data-id="element-3">
          <h2 className="text-2xl font-serif font-bold text-[#0f4c4c]" data-id="element-4">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#0f4c4c]/10 transition-colors" data-id="element-5">
            <X size={24} className="text-[#0f4c4c]" data-id="element-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-88px)]" data-id="element-7">
          {children}
        </div>
      </div>
    </div>;
}
// Privacy Policy Content
function PrivacyContent() {
  return <div className="prose prose-sm max-w-none text-gray-700 space-y-6" data-id="element-8">
      <p className="text-sm text-gray-500" data-id="element-9">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section data-id="element-10">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-11">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-12">
          Recopilamos información que nos proporcionas directamente cuando
          realizas una compra, incluyendo:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-13">
          <li data-id="element-14">Nombre y dirección de correo electrónico</li>
          <li data-id="element-15">
            Información de pago (procesada de forma segura por nuestros
            proveedores de pago)
          </li>
        </ul>
      </section>

      <section data-id="element-16">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-17">
          2. Uso de la Información
        </h3>
        <p data-id="element-18">Utilizamos la información recopilada para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-19">
          <li data-id="element-20">Procesar y entregar tu compra</li>
          <li data-id="element-21">Enviarte el PDF y cualquier actualización relacionada</li>
          <li data-id="element-22">Responder a tus consultas y solicitudes</li>
          <li data-id="element-23">Mejorar nuestros productos y servicios</li>
        </ul>
      </section>

      <section data-id="element-24">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-25">
          3. Protección de Datos
        </h3>
        <p data-id="element-26">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>
      </section>

      <section data-id="element-27">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-28">
          4. Compartir Información
        </h3>
        <p data-id="element-29">
          No vendemos, comercializamos ni transferimos tu información personal a
          terceros, excepto a proveedores de servicios de confianza que nos
          ayudan a operar nuestro sitio web y procesar pagos.
        </p>
      </section>

      <section data-id="element-30">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-31">
          5. Tus Derechos
        </h3>
        <p data-id="element-32">Tienes derecho a:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-33">
          <li data-id="element-34">Acceder a tus datos personales</li>
          <li data-id="element-35">Rectificar datos inexactos</li>
          <li data-id="element-36">Solicitar la eliminación de tus datos</li>
          <li data-id="element-37">Oponerte al procesamiento de tus datos</li>
        </ul>
      </section>

      <section data-id="element-38">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-39">6. Cookies</h3>
        <p data-id="element-40">
          Utilizamos cookies esenciales para el funcionamiento del sitio. No
          utilizamos cookies de seguimiento ni publicidad.
        </p>
      </section>
    </div>;
}
// Terms of Service Content
function TermsContent() {
  return <div className="prose prose-sm max-w-none text-gray-700 space-y-6" data-id="element-41">
      <p className="text-sm text-gray-500" data-id="element-42">
        Última actualización:{' '}
        {new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
      </p>

      <section data-id="element-43">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-44">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-45">
          Al acceder y utilizar este sitio web y realizar una compra, aceptas
          estos términos y condiciones en su totalidad. Si no estás de acuerdo
          con estos términos, no debes usar este sitio.
        </p>
      </section>

      <section data-id="element-46">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-47">
          2. Producto Digital
        </h3>
        <p data-id="element-48">
          El producto "Límites que suben la autoestima" es un PDF descargable.
          Al completar tu compra:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-49">
          <li data-id="element-50">Recibirás acceso inmediato al archivo PDF</li>
          <li data-id="element-51">El producto es para uso personal únicamente</li>
          <li data-id="element-52">
            No está permitida la redistribución, reventa o compartir el
            contenido
          </li>
        </ul>
      </section>

      <section data-id="element-53">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-54">
          3. Política de Reembolso
        </h3>
        <p data-id="element-55">
          Ofrecemos una garantía de satisfacción de 7 días. Si el producto no
          cumple con tus expectativas, puedes solicitar un reembolso completo
          dentro de los 7 días posteriores a la compra, sin preguntas.
        </p>
      </section>

      <section data-id="element-56">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-57">
          4. Propiedad Intelectual
        </h3>
        <p data-id="element-58">
          Todo el contenido del PDF, incluyendo texto, gráficos y diseño, está
          protegido por derechos de autor. Queda prohibida la reproducción total
          o parcial sin autorización expresa.
        </p>
      </section>

      <section data-id="element-59">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-60">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-61">
          Este producto es de carácter educativo e informativo. No sustituye el
          consejo profesional de un psicólogo o terapeuta. Los resultados pueden
          variar según cada persona.
        </p>
      </section>

      <section data-id="element-62">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-63">
          6. Modificaciones
        </h3>
        <p data-id="element-64">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Los cambios entrarán en vigor inmediatamente después de su
          publicación en el sitio.
        </p>
      </section>

      <section data-id="element-65">
        <h3 className="text-lg font-bold text-[#0f4c4c] mb-3" data-id="element-66">
          7. Ley Aplicable
        </h3>
        <p data-id="element-67">
          Estos términos se rigen por las leyes aplicables en tu jurisdicción.
          Cualquier disputa se resolverá mediante los tribunales competentes.
        </p>
      </section>
    </div>;
}
export function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#faf8f5] text-[#1a2e2e]" data-id="element-68">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#faf8f5]/90 backdrop-blur-md border-b border-[#0f4c4c]/10" data-id="element-69">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-70">
          <div className="flex justify-between items-center h-16" data-id="element-71">
            <div className="flex items-center" data-id="element-72">
              <span className="font-serif text-xl font-bold text-[#0f4c4c]" data-id="element-73">
                Límites Sanos
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-74">
              <button onClick={() => scrollToSection('problema')} className="text-[#0f4c4c] hover:text-[#d4a574] transition-colors text-sm font-medium" data-id="element-75">
                ¿Para quién es?
              </button>
              <button onClick={() => scrollToSection('solucion')} className="text-[#0f4c4c] hover:text-[#d4a574] transition-colors text-sm font-medium" data-id="element-76">
                El Método
              </button>
              <button onClick={() => scrollToSection('oferta')} className="bg-[#0f4c4c] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#0a3535] transition-colors" data-id="element-77">
                Obtener Manual
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden" data-id="element-78">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-[#0f4c4c]" data-id="element-79">
                {isMenuOpen ? <X size={24} data-id="element-80" /> : <Menu size={24} data-id="element-81" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && <div className="md:hidden bg-[#faf8f5] border-b border-[#0f4c4c]/10 px-4 py-4 space-y-4 shadow-lg" data-id="element-82">
            <button onClick={() => scrollToSection('problema')} className="block w-full text-left text-[#0f4c4c] font-medium py-2" data-id="element-83">
              ¿Para quién es?
            </button>
            <button onClick={() => scrollToSection('solucion')} className="block w-full text-left text-[#0f4c4c] font-medium py-2" data-id="element-84">
              El Método
            </button>
            <button onClick={() => scrollToSection('oferta')} className="block w-full text-center bg-[#0f4c4c] text-white px-5 py-3 rounded-lg font-medium" data-id="element-85">
              Obtener Manual
            </button>
          </div>}
      </nav>

      {/* Hero Section */}
      <header className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-id="element-86">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#0f4c4c]/5 rounded-l-full blur-3xl -z-10 transform translate-x-1/3" data-id="element-87"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-88">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-89">
            <div className="space-y-8 animate-fade-in-up" data-id="element-90">
              <div className="inline-flex items-center space-x-2 bg-[#0f4c4c]/10 px-4 py-1.5 rounded-full" data-id="element-91">
                <Star size={14} className="text-[#0f4c4c]" fill="#0f4c4c" data-id="element-92" />
                <span className="text-xs font-bold tracking-wide text-[#0f4c4c] uppercase" data-id="element-93">
                  Manual Práctico PDF
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight text-[#0f4c4c]" data-id="element-94">
                Si decir <span className="italic text-[#d4a574]" data-id="element-95">NO</span> te
                deja sola… <br data-id="element-96" />
                <span className="relative inline-block" data-id="element-97">
                  no era compañía.
                  <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#d4a574]" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-98">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="3" fill="none" data-id="element-99" />
                  </svg>
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-lg" data-id="element-100">
                Cómo dejar de ser "la buena" para ser tú. Un manual para poner
                límites sin quedarte temblando cuando se enojan.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-101">
                <button onClick={() => scrollToSection('oferta')} className="bg-[#0f4c4c] text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-[#0a3535] transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-102">
                  Quiero recuperar mi paz <ArrowRight size={20} data-id="element-103" />
                </button>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-104">
                  <Shield size={16} data-id="element-105" />
                  <span data-id="element-106">Compra segura y garantizada</span>
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-md lg:max-w-full" data-id="element-107">
              <div className="relative z-10 transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-108">
                <div className="absolute inset-0 bg-[#0f4c4c] rounded-2xl transform translate-x-4 translate-y-4 -z-10" data-id="element-109"></div>
                <img src="https://cdn.magicpatterns.com/uploads/akCDPex7kQEQdEjyQghjkB/FOTO_FINAL_E-BOOK_HELLOYD.png" alt="Límites que suben la autoestima Ebook" className="w-full rounded-2xl shadow-2xl border border-gray-100" data-id="element-110" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-[#faf8f5] p-4 rounded-xl shadow-xl border border-[#0f4c4c]/10 z-20 max-w-[200px]" data-id="element-111">
                <div className="flex items-center gap-1 mb-1" data-id="element-112">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} className="text-[#d4a574]" fill="#d4a574" data-id="element-113" />)}
                </div>
                <p className="text-xs font-medium text-gray-800" data-id="element-114">
                  "El respeto no se negocia con tono dulce."
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section id="problema" className="py-20 bg-white" data-id="element-115">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-116">
          <div className="text-center mb-16" data-id="element-117">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f4c4c] mb-4" data-id="element-118">
              Esto es para ti si...
            </h2>
            <div className="w-24 h-1 bg-[#d4a574] mx-auto" data-id="element-119"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-120">
            {["Te piden cosas y dices 'sí' por reflejo, aunque no quieras.", "En pareja, trabajo o amigas te cuesta decir lo que necesitas sin sentirte 'mala'.", 'Te da pánico el después: que se enfríen, que se ofendan, que te dejen de hablar.', 'Estás cansada de ser la que entiende, sostiene, resuelve… y se rompe en privado.'].map((item, index) => <div key={index} className="flex items-start gap-4 p-6 bg-[#faf8f5] rounded-xl border border-[#0f4c4c]/5 hover:border-[#0f4c4c]/20 transition-colors" data-id="element-121">
                <div className="bg-[#0f4c4c]/10 p-2 rounded-full shrink-0" data-id="element-122">
                  <Check size={20} className="text-[#0f4c4c]" data-id="element-123" />
                </div>
                <p className="text-gray-700 font-medium" data-id="element-124">{item}</p>
              </div>)}
          </div>

          <div className="mt-16 bg-[#0f4c4c] text-[#faf8f5] p-8 md:p-12 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-125">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" data-id="element-126"></div>

            <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 relative z-10" data-id="element-127">
              La escena real (la que nadie aplaude)
            </h3>
            <p className="text-lg leading-relaxed opacity-90 mb-6 relative z-10" data-id="element-128">
              Te presionan con "es un ratito", "no te cuesta nada", "yo en tu
              lugar sí lo haría". Y tú sabes que no quieres. Pero también sabes
              que si dices que no, viene el precio:
              <br data-id="element-129" />
              <br data-id="element-130" />
              <span className="font-serif italic text-xl text-[#d4a574]" data-id="element-131">
                la distancia. el cambio de tono. la frialdad.
              </span>
            </p>
            <p className="text-lg leading-relaxed opacity-90 relative z-10" data-id="element-132">
              Entonces haces el trueque: cambias tu paz por aprobación. Y lo
              llamas "ser buena".
              <br data-id="element-133" />
              <strong className="block mt-4 text-[#d4a574]" data-id="element-134">
                Ese trueque es el veneno lento de tu autoestima.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* The Shift / Solution */}
      <section id="solucion" className="py-20 bg-[#faf8f5]" data-id="element-135">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-136">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-137">
            <div data-id="element-138">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f4c4c] mb-6" data-id="element-139">
                Por qué lo que probaste no te sirvió
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-140">
                Te dijeron "pon límites", "sé asertiva", "trabaja tu
                autoestima". Pero esos consejos se quedan en ideas.
              </p>
              <p className="text-gray-600 mb-8" data-id="element-141">
                En la vida real, cuando alguien se molesta,{' '}
                <strong className="text-[#0f4c4c]" data-id="element-142">
                  tu cuerpo entra en modo supervivencia
                </strong>
                : explicas, sonríes, cedes, arreglas, te disculpas.
              </p>

              <div className="bg-white p-6 rounded-xl border-l-4 border-[#d4a574] shadow-sm" data-id="element-143">
                <p className="text-xl font-serif font-medium text-[#0f4c4c]" data-id="element-144">
                  "No te falta intención. Te falta estructura."
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-145">
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-[#0f4c4c]/5" data-id="element-146">
                <div className="flex items-center gap-3 mb-4" data-id="element-147">
                  <BookOpen className="text-[#d4a574]" size={24} data-id="element-148" />
                  <h3 className="text-xl font-bold text-[#0f4c4c]" data-id="element-149">
                    La Idea Central
                  </h3>
                </div>
                <p className="text-gray-600" data-id="element-150">
                  La autoestima no sube en tu cabeza. Sube cuando te respetas en
                  tiempo real. Cuando dices lo que es verdad aunque la otra
                  persona no lo celebre.
                </p>
              </div>

              <div className="bg-[#0f4c4c] p-8 rounded-2xl shadow-lg text-white transform md:translate-x-8" data-id="element-151">
                <div className="flex items-center gap-3 mb-4" data-id="element-152">
                  <Heart className="text-[#d4a574]" size={24} fill="#d4a574" data-id="element-153" />
                  <h3 className="text-xl font-bold" data-id="element-154">
                    El Mapa de Intercambios Invisibles
                  </h3>
                </div>
                <p className="opacity-90 mb-4" data-id="element-155">
                  Este PDF te hace ver que no "cedes porque eres buena". Cedes
                  porque estás comprando un pago emocional: calma, pertenencia,
                  aprobación.
                </p>
                <p className="font-medium text-[#d4a574]" data-id="element-156">
                  Cuando lo ves, cambia el juego: ya no estás "siendo
                  dramática". Estás cerrando una fuga.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objection Handling */}
      <section className="py-20 bg-[#1a2e2e] text-[#faf8f5] relative overflow-hidden" data-id="element-157">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(#d4a574 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} data-id="element-158"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-159">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8" data-id="element-160">
            "Pero si pongo límites, me dejan de hablar…"
          </h2>

          <div className="max-w-2xl mx-auto space-y-8" data-id="element-161">
            <p className="text-xl opacity-90" data-id="element-162">
              Sí. A veces pasa. Y esta es la parte adulta que nadie te dijo:
            </p>

            <div className="py-8 border-y border-[#d4a574]/30" data-id="element-163">
              <p className="text-2xl md:text-3xl font-serif italic text-[#d4a574]" data-id="element-164">
                "Si alguien solo te habla cuando cedes, no es cercanía: es
                acceso."
              </p>
            </div>

            <p className="text-lg opacity-90" data-id="element-165">
              Este PDF no te promete que todos se quedarán. Te da algo mejor:{' '}
              <br data-id="element-166" />
              <strong className="text-white" data-id="element-167">
                que tú no te pierdas para que otros se queden.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-id="element-168">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-169">
          <div className="text-center mb-16" data-id="element-170">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0f4c4c] mb-4" data-id="element-171">
              Lo que vas a conseguir
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-172">
              Herramientas concretas para dejar de negociar tu límite dentro de
              la misma conversación.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-173">
            {[{
            title: 'Claridad Inmediata',
            desc: 'Decir NO en una línea sin explicarte como si estuvieras en un juicio.',
            icon: <Check size={24} data-id="element-174" />
          }, {
            title: 'Guiones Específicos',
            desc: 'Poner límites en pareja, trabajo y amigas con palabras exactas que funcionan.',
            icon: <BookOpen size={24} data-id="element-175" />
          }, {
            title: 'Sostén Emocional',
            desc: "Sostener la incomodidad sin correr a 'arreglar' cuando hay silencio o enojo.",
            icon: <Shield size={24} data-id="element-176" />
          }].map((benefit, i) => <div key={i} className="bg-[#faf8f5] p-8 rounded-2xl hover:shadow-lg transition-shadow border border-transparent hover:border-[#d4a574]/30 group" data-id="element-177">
                <div className="w-12 h-12 bg-[#0f4c4c] rounded-full flex items-center justify-center text-[#d4a574] mb-6 group-hover:scale-110 transition-transform" data-id="element-178">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0f4c4c] mb-3" data-id="element-179">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-180">{benefit.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Offer / Product Section */}
      <section id="oferta" className="py-20 bg-[#faf8f5]" data-id="element-181">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-182">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-[#0f4c4c]/10" data-id="element-183">
            <div className="grid lg:grid-cols-2" data-id="element-184">
              <div className="bg-[#0f4c4c] p-10 text-white flex flex-col justify-between relative overflow-hidden" data-id="element-185">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-186"></div>

                <div className="relative z-10" data-id="element-187">
                  <span className="inline-block bg-[#d4a574] text-[#0f4c4c] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-6" data-id="element-188">
                    Oferta Especial
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-189">
                    Límites que suben la autoestima
                  </h2>
                  <p className="text-[#d4a574] text-xl mb-8 font-medium" data-id="element-190">
                    Edición Neutral LATAM
                  </p>

                  <div className="space-y-4 mb-8" data-id="element-191">
                    {['El Mapa de Intercambios Invisibles', "Guion Base + técnica del 'disco rayado'", 'Banco de scripts para WhatsApp', 'Respuestas para: silencio, enojo, culpa', 'Plan de práctica de 14 días'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-192">
                        <div className="bg-white/20 p-1 rounded-full" data-id="element-193">
                          <Check size={14} className="text-white" data-id="element-194" />
                        </div>
                        <span className="text-sm md:text-base opacity-90" data-id="element-195">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="relative z-10 mt-8 pt-8 border-t border-white/10" data-id="element-196">
                  <div className="flex items-end gap-4" data-id="element-197">
                    <span className="text-5xl font-bold text-white" data-id="element-198">$6.39</span>
                    <span className="text-white/60 mb-2 text-lg line-through" data-id="element-199">
                      $12
                    </span>
                    <span className="text-[#d4a574] mb-2 text-sm font-bold uppercase" data-id="element-200">
                      Pago único
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-10 flex flex-col justify-center bg-white" data-id="element-201">
                <div className="text-center mb-8" data-id="element-202">
                  <h3 className="text-2xl font-bold text-[#0f4c4c] mb-2" data-id="element-203">
                    Descarga Inmediata
                  </h3>
                  <p className="text-gray-500" data-id="element-204">
                    Empieza a leer y aplicar hoy mismo.
                  </p>
                </div>

                <a href="https://limitessanos.com/cart/43052381306923:1" target="_blank" rel="noopener noreferrer" className="w-full bg-[#d4a574] hover:bg-[#b88b5c] text-white text-xl font-bold py-5 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-4 flex items-center justify-center gap-2 no-underline" data-id="element-205">
                  <span data-id="element-206">Descargar Ahora</span>
                  <ArrowRight size={24} data-id="element-207" />
                </a>

                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-8" data-id="element-208">
                  <Lock size={14} data-id="element-209" />
                  <span data-id="element-210">Pago 100% seguro y encriptado</span>
                </div>

                <div className="bg-[#faf8f5] p-6 rounded-xl border border-[#0f4c4c]/10" data-id="element-211">
                  <div className="flex gap-4" data-id="element-212">
                    <div className="bg-[#0f4c4c]/10 p-2 rounded-full h-fit" data-id="element-213">
                      <Shield size={20} className="text-[#0f4c4c]" data-id="element-214" />
                    </div>
                    <div data-id="element-215">
                      <h4 className="font-bold text-[#0f4c4c] text-sm mb-1" data-id="element-216">
                        Garantía de 7 días
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-217">
                        Si sientes que este manual no te da las herramientas que
                        necesitas, te devolvemos tu dinero. Sin preguntas
                        incómodas.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2e2e] text-white/60 py-12 border-t border-white/10" data-id="element-218">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-219">
          <div className="grid md:grid-cols-2 gap-8 items-center mb-8" data-id="element-220">
            <div data-id="element-221">
              <h4 className="text-white font-serif text-xl font-bold mb-4" data-id="element-222">
                Límites Sanos
              </h4>
              <p className="text-sm max-w-xs" data-id="element-223">
                Un manual para dejar de vivir en "urgencias ajenas" y empezar a
                construir tu propia vida.
              </p>
            </div>
            <div className="flex md:justify-end gap-6" data-id="element-224">
              <button onClick={() => setActiveModal('terms')} className="hover:text-[#d4a574] transition-colors" data-id="element-225">
                Términos
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-[#d4a574] transition-colors" data-id="element-226">
                Privacidad
              </button>
            </div>
          </div>
          <div className="text-center md:text-left text-xs pt-8 border-t border-white/5" data-id="element-227">
            &copy; {new Date().getFullYear()} Límites Sanos. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-228">
        <PrivacyContent data-id="element-229" />
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-230">
        <TermsContent data-id="element-231" />
      </Modal>
    </div>;
}